import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Layers, Plus, FileText, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ShareProductsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', short_name: '', description: '', currency: 'ZAR', total_shares: '', unit_price: '', minimum_shares: '', maximum_shares: '', lock_in_period: '' });
  const [editProduct, setEditProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [contractFile, setContractFile] = useState(null);
  const contractInputRef = React.useRef(null);
  const { user } = useAuth();

  useEffect(() => { load(); loadPending(); }, []);

  const load = async () => {
    setLoading(true);
    try { const res = await api.get('/share-products'); setItems(res.data); }
    catch { setItems([]); }
    setLoading(false);
  };

  const loadPending = async () => {
    try {
      const res = await api.get('/pending-product-changes');
      setPendingChanges(res.data.filter(c => c.product_type === 'share_product'));
    } catch { /* ignore */ }
  };

  const handleSubmit = async () => {
    if (!form.name) { toast.error('Product name is required'); return; }
    try {
      await api.post('/share-products', {
        ...form,
        total_shares: parseInt(form.total_shares) || 0,
        unit_price: parseFloat(form.unit_price) || 0,
        minimum_shares: parseInt(form.minimum_shares) || 1,
        maximum_shares: parseInt(form.maximum_shares) || 0,
        lock_in_period: parseInt(form.lock_in_period) || 0,
      });
      toast.success('Share product created');
      setShowModal(false);
      setForm({ name: '', short_name: '', description: '', currency: 'ZAR', total_shares: '', unit_price: '', minimum_shares: '', maximum_shares: '', lock_in_period: '' });
      load();
    } catch { toast.error('Failed to create'); }
  };

  const openEditModal = (p) => {
    setEditProduct(p);
    setEditForm({
      name: p.name || '', short_name: p.short_name || '', description: p.description || '',
      total_shares: p.total_shares || '', unit_price: p.unit_price || '',
      minimum_shares: p.minimum_shares || '', maximum_shares: p.maximum_shares || '',
      lock_in_period: p.lock_in_period || '',
      effective_date: new Date().toISOString().slice(0, 10),
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {
      const payload = {
        ...editForm,
        total_shares: parseInt(editForm.total_shares) || undefined,
        unit_price: parseFloat(editForm.unit_price) || undefined,
        minimum_shares: parseInt(editForm.minimum_shares) || undefined,
        maximum_shares: parseInt(editForm.maximum_shares) || undefined,
        lock_in_period: parseInt(editForm.lock_in_period) || 0,
      };
      const res = await api.put(`/share-products/${editProduct.id}`, payload);
      if (res.data.status === 'pending') {
        toast.success('Product change submitted for checker approval');
      } else {
        toast.success('Product updated');
      }
      setShowEditModal(false);
      load(); loadPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const handleApprove = async (changeId) => {
    try {
      await api.post(`/pending-product-changes/${changeId}/approve`, { comments: '' });
      toast.success('Change approved'); loadPending(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleReject = async (changeId) => {
    try {
      await api.post(`/pending-product-changes/${changeId}/reject`, { comments: '' });
      toast.success('Change rejected'); loadPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleContractUpload = async (productId) => {
    if (!contractFile) return;
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const b64 = e.target.result.split(',')[1];
        await api.post(`/share-products/${productId}/contract/upload`, {
          file_data: b64, file_name: contractFile.name, file_type: contractFile.type,
        });
        toast.success('Contract uploaded');
        setContractFile(null); load();
      };
      reader.readAsDataURL(contractFile);
    } catch { toast.error('Failed to upload contract'); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="share-products-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Admin</span><span>/</span><span>Products</span><span>/</span><span className="text-[#1f1f1f] font-medium">Share Products</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">PRODUCT CONFIGURATION</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Share Products</h1>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="add-share-product-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Share Product
        </Button>
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="share-products-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Product Name</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Short Name</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Total Shares</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Unit Price</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Currency</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            items.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-gray-400"><Layers className="w-8 h-8 mx-auto mb-2 text-gray-300" />No share products defined.</td></tr> :
            items.map(item => (
              <tr key={item.id} className="border-b hover:bg-gray-50" data-testid={`share-product-row-${item.id}`}>
                <td className="px-4 py-3 font-medium text-sm text-[#1f1f1f]">{item.name}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{item.short_name || '-'}</td>
                <td className="px-4 py-3 text-sm text-right font-mono">{(item.total_shares || 0).toLocaleString()}</td>
                <td className="px-4 py-3 text-sm text-right font-mono">R {(item.unit_price || 0).toFixed(2)}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{item.currency || 'ZAR'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${item.is_active !== false ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{item.is_active !== false ? 'Active' : 'Inactive'}</span>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-gray-500 hover:text-[#1f1f1f]" onClick={() => openEditModal(item)} data-testid={`share-edit-${item.id}`}>Edit</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent aria-describedby="share-create-desc"><DialogHeader><DialogTitle>Create Share Product</DialogTitle></DialogHeader>
          <p id="share-create-desc" className="sr-only">Form</p>
          <div className="space-y-4">
            <div><Label>Product Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="share-name-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Short Name</Label><Input value={form.short_name} onChange={e => setForm({...form, short_name: e.target.value})} /></div>
              <div><Label>Currency</Label><Input value={form.currency} onChange={e => setForm({...form, currency: e.target.value})} /></div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Total Shares Offered</Label><Input type="number" value={form.total_shares} onChange={e => setForm({...form, total_shares: e.target.value})} /></div>
              <div><Label>Unit Price (ZAR)</Label><Input type="number" step="0.01" value={form.unit_price} onChange={e => setForm({...form, unit_price: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Min Shares</Label><Input type="number" value={form.minimum_shares} onChange={e => setForm({...form, minimum_shares: e.target.value})} /></div>
              <div><Label>Max Shares</Label><Input type="number" value={form.maximum_shares} onChange={e => setForm({...form, maximum_shares: e.target.value})} /></div>
              <div><Label>Lock-in (days)</Label><Input type="number" value={form.lock_in_period} onChange={e => setForm({...form, lock_in_period: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="share-submit-btn">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" aria-describedby="share-edit-desc">
          <DialogHeader><DialogTitle>Edit: {editProduct?.name}</DialogTitle></DialogHeader>
          <p id="share-edit-desc" className="sr-only">Edit form</p>
          {editProduct && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 text-xs text-amber-800">
                {user?.role === 'admin' ? 'Admin: changes apply immediately.' : 'Changes will be submitted for checker approval.'}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Product Name</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} /></div>
                <div><Label>Short Name</Label><Input value={editForm.short_name} onChange={e => setEditForm({...editForm, short_name: e.target.value})} /></div>
              </div>
              <div><Label>Description</Label><Input value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Total Shares</Label><Input type="number" value={editForm.total_shares} onChange={e => setEditForm({...editForm, total_shares: e.target.value})} /></div>
                <div><Label>Unit Price</Label><Input type="number" step="0.01" value={editForm.unit_price} onChange={e => setEditForm({...editForm, unit_price: e.target.value})} /></div>
                <div><Label>Effective Date</Label><Input type="date" value={editForm.effective_date} onChange={e => setEditForm({...editForm, effective_date: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Min Shares</Label><Input type="number" value={editForm.minimum_shares} onChange={e => setEditForm({...editForm, minimum_shares: e.target.value})} /></div>
                <div><Label>Max Shares</Label><Input type="number" value={editForm.maximum_shares} onChange={e => setEditForm({...editForm, maximum_shares: e.target.value})} /></div>
                <div><Label>Lock-in (days)</Label><Input type="number" value={editForm.lock_in_period} onChange={e => setEditForm({...editForm, lock_in_period: e.target.value})} /></div>
              </div>
              {(user?.role === 'admin' || user?.role === 'manager') && (
                <div className="p-3 border border-gray-200 bg-gray-50 space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">T&C / Contract Document</Label>
                  {editProduct.contract_file_name && (
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <FileText className="w-3.5 h-3.5" /><span>Current: {editProduct.contract_file_name} (v{editProduct.contract_version})</span>
                    </div>
                  )}
                  <div className="flex gap-2 items-center">
                    <Input type="file" accept=".pdf,.doc,.docx" ref={contractInputRef} onChange={e => setContractFile(e.target.files?.[0])} className="text-xs" />
                    <Button size="sm" disabled={!contractFile} onClick={() => handleContractUpload(editProduct.id)} className="text-xs bg-[#1f1f1f] text-white" data-testid="share-upload-contract-btn">Upload</Button>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleEditSubmit} className="bg-[#1f1f1f] text-white" data-testid="share-edit-submit">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Changes */}
      {pendingChanges.length > 0 && (user?.role === 'admin' || user?.role === 'manager') && (
        <div className="bg-white border border-amber-200 p-4" data-testid="share-pending-changes-section">
          <h3 className="text-sm font-bold text-[#1f1f1f] mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-600" />Pending Share Product Changes ({pendingChanges.length})
          </h3>
          <div className="space-y-2">
            {pendingChanges.map(pc => (
              <div key={pc.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-100" data-testid={`share-pending-${pc.id}`}>
                <div>
                  <p className="text-sm font-medium">{pc.product_name}</p>
                  <p className="text-xs text-gray-500">By: {pc.maker_name} ({pc.maker_role})</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleApprove(pc.id)} className="bg-green-600 text-white text-xs">Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => handleReject(pc.id)} className="text-xs text-red-600 border-red-200">Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
export default ShareProductsPage;
